import React, { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  HeaderGroup,
  RowData,
  useReactTable,
  ExpandedState,
  getExpandedRowModel,
  SortingState,
} from '@tanstack/react-table';
import { AllieSkeleton } from '../ProgressIndicators/Skeleton';
import { DataGridTypes } from './enums';
import { DataGridEmpty } from './components/DataGridEmpty';
import { DataGridError } from './components/DataGridError';
import { AllieDataGridProps, LoadServerRowsProps, SortingProps } from './AllieDataGrid.types';
import { DataGridPagination } from './components/DataGridPagination';
import { StyledFoot, StyledHead, StyledTable } from './AllieDataGrid.styles';
import { DataGridToolbar } from './components/DataGridToolbar';
import { AllieSpinner } from '../ProgressIndicators/Spinner';
import {
  DataGridHeaderGroup,
  DataGridRowModel,
  DataGridCellWrapper,
  DataGridHeaderWrapper,
} from './components';
import { DataGridHorizontalScroll } from './components/DataGridHorizontalScroll';
import { AllieCard } from '../Card';
import { JSX } from 'react/jsx-runtime';

export const DataGrid: FC<AllieDataGridProps> = ({
  columns,
  dataGridType = DataGridTypes.AUTO,
  loadServerRows,
  filters,
  sorting = [],
  limit = 10,
  showLoading = true,
  showSkeleton = false,
  emptyConfig,
  errorConfig,
  ancestorRef,
  headerItems,
  footerItems,
  title,
  enableRowSelection,
  onSelected,
  downloadQuery,
  downloadLabel,
  enableXlsxDownload,
  showTitle = true,
  getRowCanExpand,
  renderSubComponent,
  reset,
  sortConfig,
  rowsPerPageText,
  abortSignal,
  renderFootRow,
  renderHeadRow,
  className,
  pageLabel,
  headerStyle,
  showFooter = true,
  forceLoading = false,
  getRowId: getRowIdProp,
  selectedRowIds,
  onSelectionChange,
  isRowSelectable,
  stickyFirstColumn,
  horizontalScrollControls,
}) => {
  const [totalItems, setTotalItems] = useState(0);
  const [sortFields, setSortFields] = useState<SortingProps[]>(sorting);
  const [isFetchFailed, setIsFetchFailed] = useState(false);
  const [data, setData] = useState<RowData[]>([]);
  const [columnsTyped, setColumnsTyped] = useState<ColumnDef<any, any>[]>([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tableSorting, setTableSorting] = useState<SortingState>([]);

  const abortSignalRef = useRef(abortSignal);
  abortSignalRef.current = abortSignal;

  const isControlledSelection = selectedRowIds !== undefined;

  const controlledRowSelection = useMemo(() => {
    if (!isControlledSelection) return undefined;
    const sel: Record<string, boolean> = {};
    selectedRowIds!.forEach((id) => {
      sel[String(id)] = true;
    });
    return sel;
  }, [isControlledSelection, selectedRowIds]);

  const firstPage = 0;
  const lastPage = Math.ceil(totalItems / limit) - 1;

  const query = useCallback(
    async ({
      page = 0,
      limit: currentLimit,
      filters: currentFilters = {},
      currSorting = [],
    }: {
      page: number;
      limit: number;
      filters?: Record<string, unknown>;
      currSorting?: SortingProps[];
    }) => {
      try {
        setIsLoading(true);
        const params: LoadServerRowsProps = { page, limit: currentLimit };
        if (Object.keys(currentFilters).length) params.filters = currentFilters;
        if (currSorting.length) params.sort = currSorting;
        const fetchedData = await loadServerRows(params);
        setTotalItems(fetchedData.totalItems || 0);
        setData(fetchedData.data || []);
        setIsFetchFailed(false);
      } catch (e) {
        const aborted = (e as Error)?.name === 'AbortError' || abortSignalRef.current?.aborted;
        if (!aborted) setIsFetchFailed(true);
      } finally {
        setFirstLoad(false);
        setIsLoading(false);
      }
    },
    [loadServerRows, filters]
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [filters]);

  useEffect(() => {
    const columnHelper = createColumnHelper<typeof data>();
    setColumnsTyped(
      columns.map((column: any) =>
        columnHelper.accessor(column.field as any, {
          cell: (cellContext: any) => {
            const { type, ...props } = column.cell || {};
            return (
              <DataGridCellWrapper
                type={column.cell?.type}
                value={cellContext.getValue()}
                data={cellContext.row.original}
                row={cellContext.row}
                cellProps={props}
              />
            );
          },
          header: (headerContext: any) => {
            const { type, ...props } = column.header || {};
            return (
              <DataGridHeaderWrapper
                type={type}
                title={column.header.title}
                headerProps={{ ...props, ...headerContext }}
              />
            );
          },
          enableSorting: !!column.sortable,
          size: column.width,
          meta: {
            sortDirection: column.sortDirection,
            justify: column.justify,
            suffix: column.header?.suffix,
          },
        })
      )
    );
  }, [columns]);

  const handleRowSelection = (rowSelected: any, newValue: any) => {
    return { ...rowSelected, ...newValue() };
  };

  const table = useReactTable({
    data,
    columns: columnsTyped as any,
    getRowId: getRowIdProp,
    state: {
      pagination: {
        pageIndex: currentPage,
        pageSize: limit,
      },
      expanded,
      rowSelection: isControlledSelection ? controlledRowSelection! : rowSelection,
      sorting: tableSorting,
    },
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableColumnResizing: false,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: lastPage + 1,
    autoResetPageIndex: false,
    onExpandedChange: setExpanded,
    getSubRows: (row: any) => row.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: isRowSelectable
      ? (row: any) => isRowSelectable(row.original)
      : enableRowSelection,
    onRowSelectionChange: (updater) => {
      const currentSel = isControlledSelection ? controlledRowSelection! : rowSelection;
      const newValue = typeof updater === 'function' ? updater(currentSel) : updater;

      if (isControlledSelection) {
        const ids = Object.keys(newValue).filter((k) => newValue[k]);
        onSelectionChange?.(ids);
      } else {
        if (onSelected) {
          onSelected(newValue);
        }
        setRowSelection(newValue);
      }
    },
    getRowCanExpand,
    autoResetExpanded: true,
    autoResetAll: true,
    enableSorting: true,
    manualSorting: true,
    sortDescFirst: false,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(tableSorting) : updater;
      setTableSorting(newSorting);

      const apiSorting = newSorting.map((sort) => ({
        field: sort.id,
        direction: sort.desc ? ('desc' as const) : ('asc' as const),
      }));

      setSortFields(apiSorting);
    },
  });

  useEffect(() => {
    query({ page: currentPage, limit, filters, currSorting: sortFields });
  }, [query, currentPage, limit, filters, sortFields]);

  useEffect(() => {
    ancestorRef?.current?.scrollIntoView({ behavior: 'smooth' });
    table.toggleAllRowsExpanded(false);
    if (!isControlledSelection) {
      table.toggleAllRowsSelected(false);
    }
  }, [ancestorRef, data]);

  useEffect(() => {
    if (reset) {
      setCurrentPage(0);
    }
  }, [reset]);

  const getNextPage = () => setCurrentPage(currentPage + 1);
  const getPreviousPage = () => setCurrentPage(currentPage - 1);
  const getFirstPage = () => setCurrentPage(firstPage);
  const getLastPage = () => setCurrentPage(lastPage);

  const cardHeader = (isEmpty = false) => ({
    children: (
      <DataGridToolbar
        title={title}
        downloadQuery={!isEmpty && downloadQuery ? downloadQuery : undefined}
        downloadLabel={downloadLabel ?? undefined}
        enableXlsxDownload={enableXlsxDownload}
        showTitle={showTitle}
        sortConfig={sortConfig}
        columns={table.getAllColumns()}
        onChangeSort={setSortFields}
      >
        {!isEmpty && headerItems}
      </DataGridToolbar>
    ),
    style: { padding: 0 },
  });

  const tableHeader = () => (
    <StyledHead>
      {table.getHeaderGroups().map((headerGroup: HeaderGroup<unknown>) => (
        <DataGridHeaderGroup key={headerGroup.id} group={headerGroup} headerStyle={headerStyle} />
      ))}
    </StyledHead>
  );

  const cardFooter = () => ({
    children: showFooter ? (
      <StyledFoot>
        <DataGridPagination
          table={table}
          page={currentPage}
          getFirstPage={getFirstPage}
          getPreviousPage={getPreviousPage}
          getNextPage={getNextPage}
          getLastPage={getLastPage}
          footerItems={footerItems}
          rowsPerPageText={rowsPerPageText}
          setCurrentPage={setCurrentPage}
          pageLabel={pageLabel}
        />
      </StyledFoot>
    ) : null,
    style: { padding: 0 },
  });

  const tableContent = (body: any) => {
    const tableElement = (
      <StyledTable layout={dataGridType} $stickyFirstColumn={stickyFirstColumn}>
        {tableHeader()}
        <tbody>{body()}</tbody>
      </StyledTable>
    );

    if (!horizontalScrollControls?.enabled) {
      return tableElement;
    }

    return (
      <DataGridHorizontalScroll
        scrollForwardAriaLabel={horizontalScrollControls.scrollForwardAriaLabel}
        scrollBackAriaLabel={horizontalScrollControls.scrollBackAriaLabel}
      >
        {tableElement}
      </DataGridHorizontalScroll>
    );
  };

  const tableCard = (header: any, body: any, footer: any) => (
    <AllieCard
      header={header}
      className={className}
      backgroundColor="transparent"
      content={{
        style: {
          padding: 0,
          margin: 0,
          overflowX: horizontalScrollControls?.enabled ? 'visible' : 'scroll',
        },
        children: tableContent(body),
      }}
      footer={footer}
      style={{ boxShadow: 'none', marginInline: 0, borderRadius: 0 }}
    />
  );

  const skeleton: JSX.Element[] = [];
  for (let i = 0; i < limit; ++i) {
    skeleton.push(
      <tr style={{ padding: 0 }}>
        <td colSpan={100} style={{ height: 52, padding: '0 20px' }}>
          <AllieSkeleton
            animated
            style={{
              width: '100%',
              height: '60%',
            }}
          />
        </td>
      </tr>
    );
  }

  // Show skeleton
  if (showSkeleton) return tableCard(cardHeader(true), () => skeleton, cardFooter());

  // Loading state
  if ((isLoading && showLoading) || forceLoading) {
    if (firstLoad) {
      return tableCard(cardHeader(true), () => skeleton, cardFooter());
    } else {
      return tableCard(
        cardHeader(),
        () => (
          <>
            <AllieSpinner
              name="dots"
              style={{
                position: 'absolute',
                left: '47.5%',
                top: '47.5%',
                transform: 'scale(3)',
              }}
            />
            <DataGridRowModel
              rowModel={table.getRowModel()}
              renderSubComponent={renderSubComponent}
              renderFootRow={renderFootRow}
              headerStyle={headerStyle}
            />
          </>
        ),
        cardFooter()
      );
    }
  }

  // Error state
  if (isFetchFailed) {
    return tableCard(
      cardHeader(),
      () => (
        <tr>
          <td colSpan={100}>
            <DataGridError
              principalText={errorConfig?.principalText}
              secondaryText={errorConfig?.secondaryText}
              refreshText={errorConfig?.refreshText}
              refreshFn={() =>
                query({
                  page: currentPage,
                  limit,
                  filters,
                  currSorting: sortFields,
                })
              }
            />
          </td>
        </tr>
      ),
      cardFooter()
    );
  }

  // Empty state
  if (table.getRowModel().rows.length === 0 && !firstLoad) {
    return tableCard(
      cardHeader(true),
      () => (
        <tr>
          <td colSpan={100}>
            <DataGridEmpty
              principalText={emptyConfig?.principalText}
              secondaryText={emptyConfig?.secondaryText}
              action={emptyConfig?.action}
              isFiltered={Object.keys(filters || {}).length > 0}
            />
          </td>
        </tr>
      ),
      cardFooter()
    );
  }

  // Populated state
  return tableCard(
    cardHeader(false),
    () => (
      <DataGridRowModel
        rowModel={table.getRowModel()}
        renderSubComponent={renderSubComponent}
        renderFootRow={renderFootRow}
        renderHeadRow={renderHeadRow}
        headerStyle={headerStyle}
      />
    ),
    cardFooter()
  );
};
